class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay, .drawer__close')) this.close();
    });
    this.setHeaderIconEvent();
  }

  setHeaderIconEvent() {
    const cartIcon = document.querySelector('[aria-controls="CartDrawer"]');
    if (!cartIcon) return;

    cartIcon.setAttribute('role', 'button');
    cartIcon.setAttribute('aria-haspopup', 'dialog');

    cartIcon.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartIcon);
    });

    cartIcon.addEventListener('keydown', (event) => {
      if (event.code === 'Space' || event.code === 'Enter') {
        event.preventDefault();
        this.open(cartIcon);
      }
    });
  }

  async open(triggeredBy, contentsAreFresh = false) {
    this.openVersion = (this.openVersion || 0) + 1;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('open')) {
      cartDrawerNote.click();
    }

    // Show the drawer shell immediately; server refresh must not delay opening.
    this.classList.add('animate', 'active');

    this.addEventListener(
      'transitionend',
      () => {
        const focusElement = this.querySelector('[tabindex="-1"]') || this.querySelector('.drawer__inner');
        if (focusElement) focusElement.focus();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    if (!contentsAreFresh && !this.openingCart) {
      this.setAttribute('aria-busy', 'true');
      // History can restore empty server markup even though the cart now has
      // items. Do not present that snapshot as the current cart while fetching.
      const cachedEmpty = this.querySelector('.drawer__inner-empty');
      let loadingStatus;
      if (cachedEmpty) {
        cachedEmpty.hidden = true;
        cachedEmpty.style.display = 'none';
        loadingStatus = document.createElement('p');
        loadingStatus.setAttribute('role', 'status');
        loadingStatus.textContent = 'Loading your cart…';
        loadingStatus.style.cssText = 'text-align:center;padding:3rem 2rem;';
        this.querySelector('.drawer__inner')?.appendChild(loadingStatus);
      }
      this.openingCart = this.refreshBeforeOpen()
        .catch(error => {
          console.warn('[GBJ cart] Drawer refresh failed', error);
          if (loadingStatus?.isConnected) loadingStatus.textContent = 'Cart could not load. Please close and reopen it to try again.';
          const errors = this.querySelector('#CartDrawer-CartErrors');
          if (errors) errors.textContent = 'Cart could not refresh. Please close and reopen it to try again.';
        })
        .finally(() => {
          this.openingCart = null;
          this.removeAttribute('aria-busy');
        });
    }
  }

  async refreshBeforeOpen() {
    const root = window.Shopify?.routes?.root || '/';
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const query = new URLSearchParams({ sections: 'cart-drawer,cart-icon-bubble', sections_url: root + 'cart', source: 'gbj', t: String(Date.now()) });
      const response = await fetch(root + 'cart?' + query, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart sections failed: ' + response.status);
      const sections = await response.json();
      const parser = new DOMParser();
      const fresh = parser.parseFromString(sections['cart-drawer'] || '', 'text/html').querySelector('cart-drawer');
      if (!fresh) throw new Error('Cart drawer section missing');
      // Do not let an outdated refresh replace a subsequently added product.
      if (window.__GBJ_EXPRESS_CHECKOUT_LOCK__) return;
      this.innerHTML = fresh.innerHTML;
      this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      const header = document.getElementById('cart-icon-bubble');
      const headerDoc = parser.parseFromString(sections['cart-icon-bubble'] || '', 'text/html');
      const freshHeader = headerDoc.querySelector('#cart-icon-bubble') || headerDoc.querySelector('.shopify-section');
      if (header && freshHeader) header.innerHTML = freshHeader.innerHTML;
      const countText = headerDoc.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent;
      // The cart-icon section omits its badge for an empty cart. Absence is
      // therefore an authoritative zero, not a reason to leave a stale count.
      const count = countText == null ? 0 : Number(countText.trim());
      if (Number.isInteger(count) && count >= 0) document.querySelectorAll('cart-count.header__cart-count').forEach(badge => {
        badge.textContent = String(count);
        badge.style.display = count ? '' : 'none';
        badge.classList.toggle('hidden', count === 0);
      });
      window.moveGiftToTop?.();
    } finally { clearTimeout(timer); }
  }

  close() {
    this.openVersion = (this.openVersion || 0) + 1;
    this.classList.remove('animate', 'active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.closest('details').addEventListener('toggle', (event) => {
      const isOpen = event.target.hasAttribute('open');
      cartDrawerNote.setAttribute('aria-expanded', isOpen);
    });
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.classList.toggle('is-empty', parsedState.item_count === 0);

    if (parsedState.item_count === 0) {
      // Cart just became empty — the "empty cart" markup only exists in the
      // freshly rendered section HTML, not as a live DOM node we can patch.
      // Swap the whole drawer inner content instead of patching narrow selectors.
      const cartDrawerHTML = parsedState.sections['cart-drawer'];
      const drawerInner = this.querySelector('.drawer__inner');
      if (cartDrawerHTML && drawerInner) {
        const doc = new DOMParser().parseFromString(cartDrawerHTML, 'text/html');
        const newInner = doc.querySelector('.drawer__inner');
        if (newInner) {
          drawerInner.innerHTML = newInner.innerHTML;
        } else {
          console.warn('[CartDrawer] Could not find .drawer__inner in the empty-cart section response.');
        }
      }
    } else {
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
      document.getElementById('cart-drawer-items')?.classList.remove('is-empty');

      // When the cart previously emptied, we swapped in the "your cart is
      // empty" markup (.drawer__inner-empty) by replacing the whole
      // .drawer__inner innerHTML. That block is never removed afterwards,
      // so once an item is added back it stays in the DOM, sitting above
      // the real item list in the flex layout and hiding/pushing it out of
      // view — exactly the "drawer opens, only footer/total show" bug.
      // Remove it explicitly now that the cart is no longer empty.
      this.querySelector('.drawer__inner-empty')?.remove();

      // If the drawer was previously emptied, its section containers
      // (#cart-drawer-items, .drawer__footer, etc.) were wiped out by the
      // "empty cart" markup swap and no longer exist in the DOM. Rebuild
      // drawer__inner from the fresh section HTML first, otherwise the
      // section patching below silently no-ops (container is null) and
      // newly added items never appear until a page reload.
      if (!document.getElementById('cart-drawer-items')) {
        const cartDrawerHTML = parsedState.sections['cart-drawer'];
        const drawerInner = this.querySelector('.drawer__inner');
        if (cartDrawerHTML && drawerInner) {
          const doc = new DOMParser().parseFromString(cartDrawerHTML, 'text/html');
          const newInner = doc.querySelector('.drawer__inner');
          if (newInner) drawerInner.innerHTML = newInner.innerHTML;
        }
      }

      this.getSectionsToRender().forEach((section) => {
        const container = document.getElementById(section.id);
        if (!container) return;

        const sectionHTML = parsedState.sections[section.section];
        if (!sectionHTML) {
          console.warn(`[CartDrawer] Missing section "${section.section}" in cart response — check this section id exists and is included in the sections request.`);
          return;
        }

        try {
          const elementToReplace = container.querySelector(section.selector) || container;
          elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
        } catch (error) {
          console.error(`[CartDrawer] Failed to update section "${section.section}":`, error);
        }
      });
    }

    // Always open the drawer, even if one section failed above
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.open(undefined, true);
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const target = doc.querySelector(selector);
    if (!target) {
      throw new Error(`Selector "${selector}" not found in returned section HTML`);
    }
    return target.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer-items',
        section: 'cart-drawer',
        selector: '.drawer__contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'cart-drawer-footer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
    ];
  }

  getSectionDOM(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // 'cart-items' and 'product-form' both already render the drawer directly
      // (via renderContents / updateQuantity) — re-fetching here races with that
      // and can overwrite good content with a stale/blank fetch.
      if (event.source === 'cart-items' || event.source === 'product-form') return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' })
      .then((response) => response.text())
      .then((responseText) => {
        if (!this.isConnected || window.__GBJ_EXPRESS_CHECKOUT_LOCK__) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer-items',
        section: 'cart-drawer',
        selector: '.drawer__contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'cart-drawer-footer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    if (window.__GBJ_EXPRESS_CHECKOUT_LOCK__) return;
    const requestVersion = this.quantityRequestVersion = (this.quantityRequestVersion || 0) + 1;
    const currentQuantityInput =
      this.querySelector(`#Drawer-quantity-${line}`) || this.querySelector(`#Quantity-${line}`);
    const changedVariantId = variantId || currentQuantityInput?.dataset?.quantityVariantId;
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        if (!this.isConnected || requestVersion !== this.quantityRequestVersion) return;
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Drawer-quantity-${line}`) || document.getElementById(`Quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Keep the quantity selector on the currently open product page in
        // sync with the total quantity of this variant in the cart.
        this.syncProductPageQuantity(parsedState, changedVariantId);

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');

        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        if (parsedState.item_count === 0) {
          // Cart just became empty — swap the whole drawer inner content so the
          // "empty cart" markup (which only exists in the fresh section HTML) renders.
          const cartDrawerHTML = parsedState.sections['cart-drawer'];
          const drawerInner = cartDrawerWrapper ? cartDrawerWrapper.querySelector('.drawer__inner') : null;
          if (cartDrawerHTML && drawerInner) {
            const doc = new DOMParser().parseFromString(cartDrawerHTML, 'text/html');
            const newInner = doc.querySelector('.drawer__inner');
            if (newInner) {
              drawerInner.innerHTML = newInner.innerHTML;
            } else {
              console.warn('[CartDrawer] Could not find .drawer__inner in the empty-cart section response.');
            }
          }
        } else {
          // Same fix as CartDrawer.renderContents: rebuild drawer__inner if the
          // drawer was previously emptied, since its section containers no
          // longer exist and document.getElementById(section.id) below would
          // throw / silently miss the update otherwise.
          if (!document.getElementById('cart-drawer-items')) {
            const cartDrawerHTML = parsedState.sections['cart-drawer'];
            const drawerInner = cartDrawerWrapper ? cartDrawerWrapper.querySelector('.drawer__inner') : null;
            if (cartDrawerHTML && drawerInner) {
              const doc = new DOMParser().parseFromString(cartDrawerHTML, 'text/html');
              const newInner = doc.querySelector('.drawer__inner');
              if (newInner) drawerInner.innerHTML = newInner.innerHTML;
            }
          }

          this.getSectionsToRender().forEach((section) => {
            const container = document.getElementById(section.id);
            if (!container) return;
            const elementToReplace = container.querySelector(section.selector) || container;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        if (parsedState.item_count !== 0) {
          const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        } else if (cartDrawerWrapper) {
          const emptyLink = cartDrawerWrapper.querySelector('.drawer__inner-empty a');
          if (emptyLink) trapFocus(cartDrawerWrapper, emptyLink);
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  syncProductPageQuantity(cartState, variantId) {
    if (!cartState?.items) return;

    const normalizedVariantId = variantId ? String(variantId) : null;

    document.querySelectorAll('input[name="quantity"]').forEach((input) => {
      const productForm = input.form;
      const selectedVariantInput = productForm?.querySelector('input[name="id"]');
      const inputVariantId = input.dataset.quantityVariantId || selectedVariantInput?.value;

      if (!inputVariantId) return;
      if (normalizedVariantId && String(inputVariantId) !== normalizedVariantId) return;

      const cartQuantity = cartState.items.reduce((total, item) => {
        return String(item.variant_id) === String(inputVariantId)
          ? total + Number(item.quantity || 0)
          : total;
      }, 0);

      const minimum = Math.max(Number(input.min || input.dataset.min || 1), 1);
      const maximum = Number(input.max || input.dataset.max || Infinity);
      let displayedQuantity = cartQuantity > 0 ? cartQuantity : minimum;

      if (Number.isFinite(maximum)) {
        displayedQuantity = Math.min(displayedQuantity, maximum);
      }

      input.value = String(displayedQuantity);
      input.setAttribute('value', String(displayedQuantity));
      input.dataset.cartQuantity = String(cartQuantity);

      const quantityForm = input.closest('.product-form__quantity');
      const quantityLabel = quantityForm?.querySelector('[id^="quantity-label-"]');
      if (quantityLabel) {
        quantityLabel.textContent = cartQuantity > 0
          ? `${cartQuantity} item${cartQuantity === 1 ? '' : 's'} in cart`
          : 'Quantity';
      }
    });

    document.dispatchEvent(new CustomEvent('product-quantity:cart-sync', {
      detail: {
        variantId: normalizedVariantId,
      },
    }));
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        // eslint-disable-next-line no-unused-expressions
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const cartDrawerItems = document.getElementById('CartDrawer-CartItems');
    if (cartDrawerItems) cartDrawerItems.classList.add('cart__items--disabled');

    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    [...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const cartDrawerItems = document.getElementById('CartDrawer-CartItems');
    if (cartDrawerItems) cartDrawerItems.classList.remove('cart__items--disabled');

    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
